/**
 * Engagement Dashboard API Hook
 * Calls Phoenix API endpoints backed by direct SQL stored procedures.
 * Used in portfolio mode to replace Bold Reports XML data source.
 */

import { useCallback } from 'react'
import { useApi } from '@/lib/api'
import type { EngagementLeadHours, EngagementTableRow, HoursReportRow, KpiCardsRow, MonthlyChartRow, UtilizationResponse } from '@/types/ui/dashboard'

export interface EngagementDashboardFilters {
  effectiveFrom?: string      // ISO date string YYYY-MM-DD (optional: hours report can scope by engagementId alone)
  effectiveTo?: string        // ISO date string YYYY-MM-DD
  branchCode?: string
  status?: string
  billingType?: string
  engagementType?: string
  engagementLead?: string
  workerType?: string
  procurementSource?: string
  clientId?: number
  engagementId?: number
  // Collapse each change-request chain into one row keyed on the chain root, financials summed
  // across the chain. Honoured by the engagement-table endpoint only.
  rollUp?: boolean
}

function buildQueryString(filters: EngagementDashboardFilters): string {
  const params = new URLSearchParams()
  const entries = Object.entries(filters) as [string, string | number | boolean | undefined][]
  entries.forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useEngagementDashboardApi() {
  const { mainApi } = useApi()

  const getTeamHours = useCallback(
    (filters: EngagementDashboardFilters): Promise<EngagementLeadHours[]> => {
      return mainApi.get<EngagementLeadHours[]>(
        `/api/engagement-dashboard/team-hours?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  const getUtilization = useCallback(
    (filters: EngagementDashboardFilters): Promise<UtilizationResponse> => {
      return mainApi.get<UtilizationResponse>(
        `/api/engagement-dashboard/utilization?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  const getKpiCards = useCallback(
    (filters: EngagementDashboardFilters): Promise<KpiCardsRow[]> => {
      return mainApi.get<KpiCardsRow[]>(
        `/api/engagement-dashboard/kpi-cards?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  const getMonthlyData = useCallback(
    (filters: EngagementDashboardFilters): Promise<MonthlyChartRow[]> => {
      return mainApi.get<MonthlyChartRow[]>(
        `/api/engagement-dashboard/monthly-data?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  const getEngagementTable = useCallback(
    (filters: EngagementDashboardFilters): Promise<EngagementTableRow[]> => {
      return mainApi.get<EngagementTableRow[]>(
        `/api/engagement-dashboard/engagement-table?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  // Per-worker hours for a single engagement (individual dashboard Team Hours table).
  // Backed by engagement.spEngagementHoursReport, which includes removed/terminated
  // workers and a per-worker Status. Pass engagementId via filters.
  const getHoursReport = useCallback(
    (filters: EngagementDashboardFilters): Promise<HoursReportRow[]> => {
      return mainApi.get<HoursReportRow[]>(
        `/api/engagement-reports/hours?${buildQueryString(filters)}`
      )
    },
    [mainApi]
  )

  return { getTeamHours, getKpiCards, getMonthlyData, getEngagementTable, getUtilization, getHoursReport }
}
